<template>
  <teleport to="#app">
    <transition name="alert" appear @after-leave="onLeaveTransitionDone">
      <div
        v-if="!state.closing"
        ref="alertElement"
        class="alert"
        :class="[`alert-${alert.type}`]"
      >
        <div class="alert__background"></div>
        <div
          ref="contentElement"
          class="alert__content"
          :style="{
            '--translateX': `${translate.x}px`,
            '--translateY': `${translate.y}px`
          }"
        >
          <div class="alert__draghandle"></div>
          <div v-if="alert.title != null" class="popup__label">
            {{ alert.title }}
          </div>
          <div class="alert__content__inner">
            <formatted-text
              v-if="alert.text"
              class="alert__text mb-4"
              :value="alert.text"
            />

            <!-- build alert content -->
            <form class="column gap-2" @submit.prevent="confirm">
              <template v-for="(part, index) in alert.content">
                <!-- TEXT INPUT -->
                <input-text
                  v-if="part.type === 'input'"
                  :key="part.name"
                  :label="part.label"
                  :prevent-submit="true"
                  :value="(dataToReturn[part.name] as string)"
                  :autocomplete="part.autocomplete ? part.autocomplete : 'off'"
                  :placeholder="part.placeholder"
                  :autofocus="part.autofocus"
                  :class="part.class"
                  @update:value="dataToReturn[part.name] = $event"
                  @enter="confirm"
                />

                <input-number
                  v-if="part.type === 'number'"
                  :key="part.name"
                  :label="part.label"
                  :value="(dataToReturn[part.name] as number)"
                  :min="part.min"
                  :max="part.max"
                  :step="part.step"
                  :autofocus="part.autofocus"
                  :class="part.class"
                  @update:value="dataToReturn[part.name] = $event"
                  @enter="confirm"
                />

                <!-- PASSWORD -->
                <template v-if="part.type === 'password'">
                  <input
                    :key="part.name"
                    type="hidden"
                    :name="part.user || 'user'"
                    autocomplete="username"
                  />
                  <input-password
                    :key="part.name"
                    :value="(dataToReturn[part.name] as string)"
                    :label="part.label"
                    :user="part.user"
                    :autofocus="part.autofocus"
                    :name="part.name"
                    :required="part.required"
                    :autocomplete="
                      part.current ? 'current-password' : 'new-password'
                    "
                    :class="part.class"
                    @update:value="dataToReturn[part.name] = $event"
                  />
                </template>

                <!-- CHECKBOX -->
                <input-checkbox
                  v-if="part.type === 'checkbox'"
                  :key="part.name"
                  :label="part.label"
                  :label-checked="part.labelChecked"
                  :value="(dataToReturn[part.name] as boolean)"
                  :class="part.class"
                  @update:value="dataToReturn[part.name] = $event"
                />

                <!-- TEXT -->
                <formatted-text
                  v-if="part.type === 'text'"
                  :key="index"
                  class="alert__textpart"
                  :class="part.class"
                  :value="part.text"
                />
              </template>

              <div class="alert__buttonbar">
                <button
                  v-if="alert.denyText != null"
                  ref="denyButton"
                  type="button"
                  class="button alert__button"
                  @click="deny()"
                >
                  {{ alert.denyText }}
                </button>
                <button
                  v-if="alert.confirmText != null"
                  ref="confirmButton"
                  type="submit"
                  class="button alert__button"
                  :disabled="confirmDisabled"
                >
                  {{ alert.confirmText }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import {
  reactive,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  useTemplateRef
} from "vue";
import interact from "interactjs";
import { reduce } from "lodash-es";

import InputText from "@core/input/InputText.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputPassword from "@core/input/InputPassword.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import FormattedText from "@core/ui/FormattedText.vue";

import type { AlertItem } from "./alert";
import type { Interactable } from "@interactjs/types";

const props = defineProps<{
  alert: AlertItem;
}>();

const contentElement = ref<HTMLElement>();
const alertElement = ref<HTMLElement>();
const denyButtonElement = useTemplateRef('denyButton');
const confirmButtonElement = useTemplateRef('confirmButton');

const dataToReturn = reactive({} as Record<string, unknown>);

let interactible: Interactable | null = null;

const state = reactive({
  closing: false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result: false as boolean | Record<string, any>
});

const confirmDisabled = computed(() => {
  return !requiredDataEntered.value;
});

const requiredDataEntered = computed(() => {
  for (const key of requiredKeys.value) {
    if (!dataToReturn[key]) return false;
  }
  return true;
});

const requiredKeys = computed(() => {
  return reduce(
    props.alert.content,
    (keys, part) => {
      if (!("name" in part)) return keys;
      if (!part.required) return keys;
      return keys.concat([part.name]);
    },
    [] as string[]
  );
});

const contentHasAutofocus = computed(() => {
  return props.alert.content.some((p) => "autofocus" in p && p.autofocus);
});

const defaultData = computed(() => {
  return reduce(
    props.alert.content,
    (data, part) => {
      if (!("value" in part)) return data;
      if (typeof part.value !== "undefined") data[part.name] = part.value;
      return data;
    },
    {} as { [name: string]: boolean | string | number | null }
  );
});

// prepare data
for (const prop in dataToReturn) {
  delete dataToReturn[prop];
}
for (const prop in defaultData.value) {
  dataToReturn[prop] = defaultData.value[prop];
}

onMounted(() => {
  // reset coordinates
  translate.x = 0;
  translate.y = 0;

  // update data
  for (const prop in dataToReturn) {
    delete dataToReturn[prop];
  }
  for (const prop in defaultData.value) {
    dataToReturn[prop] = defaultData.value[prop];
  }

  if (!contentHasAutofocus.value) {
    nextTick(() => {
      // focus input
      console.log("content does not have autofocus", confirmButtonElement.value);
      if (denyButtonElement.value != null) denyButtonElement.value.focus();
      else if (confirmButtonElement.value != null) {
        confirmButtonElement.value.focus();
      }
      bindDragEvents();
    });
  } else {
    console.log("content has autofocus");
  }
});

onBeforeUnmount(() => {
  removeDragEvents();
});

function onLeaveTransitionDone() {
  props.alert.resolve(state.result);
}

// =============
// SUBMITTING

function confirm() {
  if (confirmDisabled.value) return;
  submit(dataToReturn);
}

function deny() {
  submit(false);
}

function submit<T>(values: boolean | Record<string, T>) {
  state.result = values;
  state.closing = true;
}

// =================
// POSITION

const translate = reactive({
  x: 0,
  y: 0
});

function bindDragEvents() {
  nextTick(() => {
    if (!contentElement.value) return;
    const restrictElement = alertElement.value;
    interactible = interact(contentElement.value).draggable({
      allowFrom: ".alert__draghandle",
      listeners: {
        move: (event) => {
          translate.x += event.dx;
          translate.y += event.dy;
        }
      },
      modifiers: [
        interact.modifiers.restrict({
          restriction: restrictElement,
          endOnly: false,
          elementRect: { top: 0, left: 0, bottom: 1, right: 1 }
        })
      ]
    });
  });
}

function removeDragEvents() {
  if (interactible) {
    interactible.unset();
    interactible = null;
  }
}
</script>

<style lang="scss">
.alert {
  $self: &;

  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  &__content {
    width: 27rem;
    min-width: min-content;
    max-width: 100vw;
    z-index: 1;
    background-color: var(--color-background-deep);
    box-shadow: 0 0.5rem 1rem var(--color-shadow-2);
    border-radius: 0.2em;
    overflow: hidden;
    touch-action: none;

    transform: translate(var(--translateX, 0), var(--translateY, 0));

    &__inner {
      padding: 2rem;
    }
  }
  &-error &__content {
    box-shadow: 0 0.5rem 1rem var(--color-shadow-2),
      0 0 3px 1px var(--color-error-light);
  }

  &__draghandle {
    touch-action: none;
    height: 2rem;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }

  &__text {
    font-weight: 600;
    color: var(--color-text);
  }
  &__textpart {
    font-weight: 400;
  }

  &__textinput {
    margin-bottom: 0.5rem;
  }

  &__buttonbar {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
  }

  &__button {
    min-width: 8rem;
    white-space: nowrap;
    margin-bottom: 0.25rem;
    margin-right: 0.25rem;
  }

  &__button + &__button {
    margin-left: auto;
  }

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-color: var(--color-backdrop);
    z-index: 0;
  }

  // animations
  &-enter-active,
  &-leave-active {
    transition: opacity 120ms;

    .alert__content {
      transition: transform 120ms;
    }
  }

  &-enter-from {
    opacity: 0;

    .alert__content {
      transform: translate3d(
          var(--translateX, 0),
          calc(var(--translateY) - 30%),
          0
        )
        scale3d(1.1, 1.1, 1);
    }
  }

  &-leave-to {
    opacity: 0;

    .alert__content {
      transform: translate3d(
        var(--translateX, 0),
        calc(var(--translateY) - 60%),
        0
      );
    }
  }
}
</style>
